/* eslint-disable max-lines-per-function */
import { computed, inject } from '@angular/core';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withDevtools,
} from '@angular-architects/ngrx-toolkit';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { setEntities, setEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, exhaustMap, pipe, tap } from 'rxjs';

import {
  Classificator,
  GeoDataDetail,
  MonitoringService,
} from './rtudata.service';

const initialState: {
  loadingMap: boolean;
  selectedScanId: number | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  cellstatus: Classificator[];
  treespecies: Classificator[];
  activeLayer:
    | 'ndvi'
    | 'thermal'
    | 'species'
    | 'empty'
    | 'weed'
    | 'multiple'
    | 'objects';
} = {
  activeLayer: 'objects',
  cellstatus: [],
  loadingMap: false,
  selectedDate: null,
  selectedScanId: null,
  selectedTime: null,
  treespecies: [],
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const RtudataStore = signalStore(
  { providedIn: 'root' },
  withDevtools('rtudataStore'),
  withState(initialState),
  withEntities<GeoDataDetail>(),
  withCallState(),
  withProps(() => ({
    monitoringService: inject(MonitoringService),
  })),

  withComputed(({ entities, selectedScanId }) => ({
    scanDates: computed(() => {
      const all = Object.values(entities());
      if (all.length === 0) {
        return [];
      }
      // Return only the session_start dates of all entities
      return all.map((data) => data.session_start);
    }),
    selectedScan: computed(() => {
      const scanSessions = Object.values(entities());
      if (scanSessions.length === 0) {
        return null;
      }
      // If selectedScanId is set, return the corresponding scan
      if (selectedScanId()) {
        return (
          scanSessions.find((data) => data.id === selectedScanId()) ?? null
        );
      }

      // Return the scan with the most recent session_start date
      return scanSessions.reduce(
        (latest, current) =>
          new Date(current.session_start) > new Date(latest.session_start)
            ? current
            : latest,
        scanSessions[0],
      );
    }),
  })),
  withComputed(({ entities, selectedDate }) => ({
    scanByDate: computed(() => {
      if (!selectedDate()) {
        return [];
      }

      const scanSessions = Object.values(entities());
      return scanSessions
        .filter((scan) => {
          const date = new Date(scan.session_start);
          return (
            date.getFullYear() === selectedDate()?.getFullYear() &&
            date.getMonth() === selectedDate()?.getMonth() &&
            date.getDate() === selectedDate()?.getDate()
          );
        })
        .sort((a, b) => {
          const aDate = new Date(a.session_start);
          const bDate = new Date(b.session_start);
          return aDate.getTime() - bDate.getTime();
        })
        .map((scan) => {
          const date = new Date(scan.session_start);
          // Format the time as HH:mm
          return {
            ...scan,
            time: date.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            }),
          };
        });
    }),
  })),
  withComputed(({ treespecies }) => ({
    speciesMap: computed(
      () => new Map(treespecies().map((specie) => [specie.id, specie.value])),
    ),
  })),
  withMethods(({ monitoringService, ...store }) => ({
    loadCellStatus: rxMethod(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap(() =>
          monitoringService.loadCellStatus().pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, { cellstatus: response });
              },
            }),
          ),
        ),
      ),
    ),
    loadSession: rxMethod<number>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap((param) =>
          monitoringService.loadSession(param).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(
                  store,
                  { selectedScanId: param },
                  setEntity(response),
                );
              },
            }),
          ),
        ),
      ),
    ),
    loadSessions: rxMethod(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap(() =>
          monitoringService.loadSessions().pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, setEntities([...response.results]));
              },
            }),
          ),
        ),
      ),
    ),
    loadTreeSpecies: rxMethod(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap(() =>
          monitoringService.loadTreeSpecies().pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, { treespecies: response });
              },
            }),
          ),
        ),
      ),
    ),
    setActiveLayer: (
      layer:
        | 'ndvi'
        | 'thermal'
        | 'species'
        | 'empty'
        | 'weed'
        | 'multiple'
        | 'objects',
    ): void => {
      patchState(store, () => ({ activeLayer: layer }));
    },
    setLoadingMap: (isLoading: boolean): void => {
      patchState(store, () => ({ loadingMap: isLoading }));
    },
    setSelected: (id: number): void => {
      patchState(store, () => ({ selectedScanId: id }));
    },
    setSelectedDate: (date: Date): void => {
      patchState(store, () => ({ selectedDate: date }));
    },
    setSelectedTime: (time: string | null): void => {
      patchState(store, () => ({ selectedTime: time }));
    },
  })),
  withHooks({
    onInit: (store) => {
      store.loadSessions({});
      store.loadCellStatus({});
      store.loadTreeSpecies({});
    },
  }),
);
